import type { ChainKey } from '@infinex/chain-config';

import type { Collectible } from './user/datasets';

export const COLLECTIBLE_COMPANIES = ['collector_crypt'] as const;
export type CollectibleCompany = (typeof COLLECTIBLE_COMPANIES)[number];

/**
 * Airdrop campaign key under which Collect Bot gacha NFT gifts are allocated.
 * Single source of truth shared by the backend (gacha allocation) and the
 * frontend (airdrop shareable). The matching campaign id lives backend-side in
 * `@infinex/collectibles` (`COLLECT_GACHA_NFT_AIRDROP_CAMPAIGN_ID`).
 */
export const COLLECT_GACHA_NFT_AIRDROP_CAMPAIGN_KEY =
  'collect-gacha-twitter-bot-nft-holding';

export const COLLECTIBLE_TYPES = [
  'card',
  'merch',
  'sealed',
  'comic',
  'comicraw',
] as const;
export type CollectibleType = (typeof COLLECTIBLE_TYPES)[number];

export const COLLECTIBLE_STATUSES = ['Valid', 'Invalid', 'Burned'] as const;
export type CollectibleStatus = (typeof COLLECTIBLE_STATUSES)[number];

/**
 * Holographic-foil shader categories for Pokémon TCG cards — the single
 * source of truth shared by the backend classifier (`deriveCollectCategory`
 * in `@infinex/collectibles`) and the frontend renderer (`CollectCardEnhancement`
 * in `@infinex/web-shared`). Mirrors the section headers of
 * simeydotme/pokemon-cards-css; each key maps to one dedicated shader.
 */
export const FOIL_CATEGORIES = [
  'basic',
  'regular-holo',
  'cosmos-holo',
  'rainbow-holo',
  'reverse-holo',
  'rainbow-alt',
  'secret-rare',
  'amazing-rare',
  'radiant-holo',
  'shiny-rare',
  'shiny-vault',
  'shiny-v',
  'shiny-vmax',
  'v-regular',
  'v-full-art',
  'v-alt-art',
  'v-max',
  'v-star',
  'trainer-gallery-holo',
  'trainer-gallery-v-regular',
  'trainer-gallery-v-max',
  'trainer-gallery-secret-rare',
  'trainer-full-art',
  'swsh-pikachu',
] as const;
export type FoilCategory = (typeof FOIL_CATEGORIES)[number];

export const COLLECTOR_CRYPT_GACHA_PURCHASE_STATUSES = [
  'created',
  'pending',
  'submitted',
  'opened',
  'review',
  'error',
] as const;
export type CollectorCryptGachaPurchaseStatus =
  (typeof COLLECTOR_CRYPT_GACHA_PURCHASE_STATUSES)[number];

/**
 * Marketplace buy, seller-listing, and buyer-offer actions share one lifecycle
 * and one table (`collector_crypt_marketplace_action`). The status enum is
 * unified; the action-type vocabulary is split into buy/seller/offer subsets
 * (for per-route validation and query scoping) plus a union covering the whole
 * table.
 */
export const COLLECTOR_CRYPT_MARKETPLACE_ACTION_STATUSES = [
  'created',
  'pending',
  'confirmed',
  'review',
  'error',
] as const;
export type CollectorCryptMarketplaceActionStatus =
  (typeof COLLECTOR_CRYPT_MARKETPLACE_ACTION_STATUSES)[number];

export const COLLECTOR_CRYPT_SHIPPING_REDEMPTION_STATUSES = [
  'created',
  'pending',
  'confirmed',
  'review',
  'error',
] as const;
export type CollectorCryptShippingRedemptionStatus =
  (typeof COLLECTOR_CRYPT_SHIPPING_REDEMPTION_STATUSES)[number];

/**
 * Collector Crypt's outbound-shipment fulfillment statuses: the documented set
 * (docs.collectorcrypt.com/vault/shipping-api) plus `Burned`, which is attested
 * from the live API but missing from the docs. Consumers narrow with
 * {@link isCollectorCryptOutboundShipmentStatus}; anything outside the union
 * maps to null rather than failing the read.
 */
export const COLLECTOR_CRYPT_OUTBOUND_SHIPMENT_STATUSES = [
  'Created',
  'PaymentPending',
  'PaymentReceived',
  'Pending',
  'Processing',
  'Burned',
  'Shipped',
  'Delivered',
  'ActionRequired',
  'Cancelled',
] as const;
export type CollectorCryptOutboundShipmentStatus =
  (typeof COLLECTOR_CRYPT_OUTBOUND_SHIPMENT_STATUSES)[number];

export function isCollectorCryptOutboundShipmentStatus(
  value: string | null | undefined
): value is CollectorCryptOutboundShipmentStatus {
  return (
    COLLECTOR_CRYPT_OUTBOUND_SHIPMENT_STATUSES as readonly string[]
  ).includes(value ?? '');
}

/** @deprecated Alias of the unified marketplace-action status vocabulary. */
export const COLLECTOR_CRYPT_MARKETPLACE_PURCHASE_STATUSES =
  COLLECTOR_CRYPT_MARKETPLACE_ACTION_STATUSES;
export type CollectorCryptMarketplacePurchaseStatus =
  CollectorCryptMarketplaceActionStatus;

/** @deprecated Alias of the unified marketplace-action status vocabulary. */
export const COLLECTOR_CRYPT_MARKETPLACE_SELLER_ACTION_STATUSES =
  COLLECTOR_CRYPT_MARKETPLACE_ACTION_STATUSES;
export type CollectorCryptMarketplaceSellerActionStatus =
  CollectorCryptMarketplaceActionStatus;

/** @deprecated Alias of the unified marketplace-action status vocabulary. */
export const COLLECTOR_CRYPT_MARKETPLACE_OFFER_ACTION_STATUSES =
  COLLECTOR_CRYPT_MARKETPLACE_ACTION_STATUSES;
export type CollectorCryptMarketplaceOfferActionStatus =
  CollectorCryptMarketplaceActionStatus;

export const COLLECTOR_CRYPT_MARKETPLACE_BUY_ACTION_TYPES = ['buy'] as const;
export type CollectorCryptMarketplaceBuyActionType =
  (typeof COLLECTOR_CRYPT_MARKETPLACE_BUY_ACTION_TYPES)[number];

export const COLLECTOR_CRYPT_MARKETPLACE_SELLER_ACTION_TYPES = [
  'list',
  'updateListing',
  'cancelListing',
  'acceptOffer',
] as const;
export type CollectorCryptMarketplaceSellerActionType =
  (typeof COLLECTOR_CRYPT_MARKETPLACE_SELLER_ACTION_TYPES)[number];

export const COLLECTOR_CRYPT_MARKETPLACE_OFFER_ACTION_TYPES = [
  'makeOffer',
  'updateOffer',
  'cancelOffer',
] as const;
export type CollectorCryptMarketplaceOfferActionType =
  (typeof COLLECTOR_CRYPT_MARKETPLACE_OFFER_ACTION_TYPES)[number];

export const COLLECTOR_CRYPT_MARKETPLACE_ACTION_TYPES = [
  ...COLLECTOR_CRYPT_MARKETPLACE_BUY_ACTION_TYPES,
  ...COLLECTOR_CRYPT_MARKETPLACE_SELLER_ACTION_TYPES,
  ...COLLECTOR_CRYPT_MARKETPLACE_OFFER_ACTION_TYPES,
] as const;
export type CollectorCryptMarketplaceActionType =
  (typeof COLLECTOR_CRYPT_MARKETPLACE_ACTION_TYPES)[number];

export const COLLECTOR_CRYPT_MARKETPLACE_OFFER_STATUSES = [
  'open',
  'pending_evidence',
  'accepted',
  'cancelled',
  'cancelled_externally',
] as const;
export type CollectorCryptMarketplaceOfferStatus =
  (typeof COLLECTOR_CRYPT_MARKETPLACE_OFFER_STATUSES)[number];

/**
 * Standalone gacha buyback lifecycle. `error` is only used when the worker can
 * prove the NFT was not bought back; ambiguous post-submit outcomes park in
 * `review` for reconciliation.
 */
export const COLLECTOR_CRYPT_GACHA_BUYBACK_STATUSES = [
  'created',
  'pending',
  'submitted',
  'confirmed',
  'review',
  'error',
] as const;
export type CollectorCryptGachaBuybackStatus =
  (typeof COLLECTOR_CRYPT_GACHA_BUYBACK_STATUSES)[number];

/**
 * Origin of a gacha buyback row:
 * - `gacha`: buyback of a card we sold via gacha (has a matching purchase row).
 * - `external`: facilitated sell-to-CC of a CC card we did not sell (e.g. won by
 *   opening a gacha on CC directly). Tracked distinctly so buyback history can
 *   label it; gacha P&L aggregates from the purchase table and is unaffected.
 */
export const COLLECTOR_CRYPT_GACHA_BUYBACK_SOURCES = [
  'gacha',
  'external',
] as const;
export type CollectorCryptGachaBuybackSource =
  (typeof COLLECTOR_CRYPT_GACHA_BUYBACK_SOURCES)[number];

export const COLLECTOR_CRYPT_GACHA_RARITIES = [
  'Epic',
  'Rare',
  'Uncommon',
  'Common',
] as const;
export type CollectorCryptGachaRarity =
  (typeof COLLECTOR_CRYPT_GACHA_RARITIES)[number];

export type GachaRevealDetails = {
  collectible: Collectible;
  /** Collector Crypt machine code from the settled open, e.g. `pokemon_250`. */
  packType: string;
  /** Insured card value to display in the reveal, when available. */
  insuredValue: string | null;
  /** The gacha roll outcome recorded on the open, not the card's printed rarity. */
  pullRarity: CollectorCryptGachaRarity | null;
  /** USD spent to open the pack (the open's locked price). */
  packPrice: string | null;
  /** Current in-app buyback offer, when Collector Crypt is quoting one. */
  buybackOffer: string | null;
};

/**
 * Raw Collector Crypt open-time provenance snapshot persisted with a gacha
 * purchase. The display/read surface is `Collectible`; this type mirrors the
 * loose `gachaNftMetadataSchema` from `@infinex/collector-crypt` so historical
 * rows and provider passthrough keys remain assignable without a migration.
 */
export interface CollectorCryptGachaNftMetadata {
  name?: string;
  description?: string | null;
  attributes?: unknown[];
  [key: string]: unknown;
}

export const COLLECT_GACHA_PACK_STATUSES = [
  'submitting_payment',
  'payment_submitted',
  'opened',
  'error',
] as const;
export type CollectGachaPackStatus =
  (typeof COLLECT_GACHA_PACK_STATUSES)[number];

export const COLLECT_GACHA_BATCH_STATUSES = [
  'pending',
  'partial',
  'opened',
  'review',
  'error',
] as const;
export type CollectGachaBatchStatus =
  (typeof COLLECT_GACHA_BATCH_STATUSES)[number];

export type CollectGachaManualBuyback =
  | { status: 'eligible'; amount: CollectAmount; expiresAt: number }
  | { status: 'pending'; buyback: CollectGachaBuybackSummary }
  | { status: 'unavailable' };

export const GACHA_TWITTER_BOT_APPROVAL_STATUSES = [
  // Setup lifecycle: a row is inserted `created` by the tRPC route (after it
  // signs the approve transaction), flipped to `pending` by the Inngest worker
  // that broadcasts it, then `active` once the delegation is confirmed on-chain.
  'created',
  'pending',
  'active',
  // Revoke lifecycle: an `active` row is flipped to `revoking` by the tRPC route
  // (after it signs the revoke transaction), then `revoked` once the Inngest
  // worker confirms the on-chain revoke.
  'revoking',
  'revoked',
  'expired',
  'exhausted',
  'failed',
  // Ambiguous submit outcome that needs manual reconciliation.
  'review',
] as const;
export type GachaTwitterBotApprovalStatus =
  (typeof GACHA_TWITTER_BOT_APPROVAL_STATUSES)[number];

/**
 * Atomic lifecycle of a single gacha open: `created` (reserved by the tRPC
 * route) → `pending` (the Inngest worker is charging/buying/opening) → a terminal
 * `opened` (success), `error` (clean failure, no funds stuck), or `review`
 * (ambiguous — needs manual reconciliation). Intra-flow progress (charged vs
 * bought vs opened) is derived from `chargeTxnHash`/`purchaseTxnHash`, not
 * from extra statuses; the specific failure cause lives in `errorReason`.
 */
export const GACHA_TWITTER_BOT_OPEN_STATUSES = [
  'created',
  'pending',
  'opened',
  'review',
  'error',
] as const;
export type GachaTwitterBotOpenStatus =
  (typeof GACHA_TWITTER_BOT_OPEN_STATUSES)[number];

/**
 * Refund lifecycle for a bot open after a user charge has landed. Null on the
 * open row means no refund has been needed/started. `submitted` records a
 * signed refund transaction before broadcast so retries can resume by
 * signature; `confirmed` means funds were returned; `review` means the refund
 * outcome itself is ambiguous and needs reconciliation.
 */
export const GACHA_TWITTER_BOT_REFUND_STATUSES = [
  'submitted',
  'confirmed',
  'review',
] as const;
export type GachaTwitterBotRefundStatus =
  (typeof GACHA_TWITTER_BOT_REFUND_STATUSES)[number];

/**
 * Custody-held Twitter bot card sell lifecycle. Null on the open row means no
 * buyback has been requested. `pending` claims the one-sale guard before any
 * signing, `submitted` records a signed sell transaction before/while it is
 * reconciled, `confirmed` means Collector Crypt bought the card back, `error`
 * is a definitive no-sale outcome, and `review` is an ambiguous outcome that
 * needs manual reconciliation.
 */
export const GACHA_TWITTER_BOT_SELL_STATUSES = [
  'pending',
  'submitted',
  'confirmed',
  'error',
  'review',
] as const;
export type GachaTwitterBotSellStatus =
  (typeof GACHA_TWITTER_BOT_SELL_STATUSES)[number];

/**
 * USDC payout lifecycle for a confirmed Twitter bot buyback. Null means payout
 * has not started. `pending` claims the payout phase, `submitted` records an
 * on-chain transfer signature, `confirmed` means the recipient has been paid or
 * a claimable allocation was created, `error` is a definitive failure before a
 * safe fallback, and `review` is an ambiguous payout that must not be retried
 * blindly.
 */
export const GACHA_TWITTER_BOT_PAYOUT_STATUSES = [
  'pending',
  'submitted',
  'confirmed',
  'error',
  'review',
] as const;
export type GachaTwitterBotPayoutStatus =
  (typeof GACHA_TWITTER_BOT_PAYOUT_STATUSES)[number];

export const GACHA_TWITTER_BOT_PAYOUT_METHODS = [
  'transfer',
  'allocation',
] as const;
export type GachaTwitterBotPayoutMethod =
  (typeof GACHA_TWITTER_BOT_PAYOUT_METHODS)[number];

export type CollectGachaPackResult =
  | {
      type: 'prize';
      nftAddress: string;
      collectible: Collectible | null;
      points: number;
      roll: string;
      rarity: CollectorCryptGachaRarity;
      openingTxnHash: string | null;
      /** Reveal-scoped manual buyback quote state; separate from collection dataset enrichment. */
      manualBuyback?: CollectGachaManualBuyback;
    }
  | {
      type: 'auto_buyback';
      /** Auto-buyback refund amount, null if unavailable. */
      buybackAmount: CollectAmount | null;
      points: number | null;
      roll: string | null;
      rarity: 'Common';
      openingTxnHash: string | null;
      nftAddress: string | null;
      collectible: Collectible | null;
    };

export interface CollectGachaPackSummary {
  packIndex: number;
  packCount: number;
  packType: string;
  isAutoBuybackOn: boolean;
  status: CollectGachaPackStatus;
  purchaseTxnHash: string | null;
  result: CollectGachaPackResult | null;
  errorReason: string | null;
}

export interface CollectGachaBatchSummary {
  /** Client-supplied idempotency key for the whole buy action. */
  requestId: string;
  status: CollectGachaBatchStatus;
  walletAddress: string;
  packType: string;
  isAutoBuybackOn: boolean;
  packCount: number;
  packs: CollectGachaPackSummary[];
}

/**
 * Transactional `collectGacha` websocket payload. `pending` and whole-batch
 * terminal failures carry only the request identifier needed to correlate the
 * originating request. `opened` and mixed-outcome `partial` events carry the
 * full (enriched) summary so the UI can render winning packs alongside failed
 * rows without sniffing for summary fields.
 */
export type CollectGachaBatchEvent =
  | { requestId: string; status: 'pending' }
  | (CollectGachaBatchSummary & { status: 'opened' | 'partial' })
  | { requestId: string; status: 'review' | 'error' };

/**
 * Product-facing summary of Collect money. Rows persist decimal values and the
 * payment asset identity only; callers resolve symbol/icon/decimals from
 * metadata by chain/address when they need display data.
 */
export interface CollectAsset {
  chain: ChainKey;
  address: string;
}

export interface CollectAmount {
  /** Amount denominated in the payment asset, as a decimal string. */
  decimalAmount: string;
  /** USD value at write time, as a decimal string. */
  decimalAmountUsd: string;
  paymentAsset: CollectAsset;
}

export interface CollectMarketplacePurchaseSummary {
  id: string;
  /** Client-supplied idempotency key for the buy. */
  requestId: string;
  nftAddress: string;
  sellerWallet: string | null;
  amount: CollectAmount;
  decimalAmount: string;
  decimalAmountUsd: string;
  paymentAssetChain: ChainKey;
  paymentAssetAddress: string;
  status: CollectorCryptMarketplacePurchaseStatus;
  purchaseTxnHash: string | null;
  createdAt: Date;
  updatedAt: Date;
}

interface CollectMarketplaceSellerActionSummaryBase {
  id: string;
  /** Client-supplied idempotency key for the seller action. */
  requestId: string;
  nftAddress: string;
  walletAddress: string;
  /** Buyer/offerer for `acceptOffer`; null for listing actions. */
  counterpartyWallet: string | null;
  actionType: CollectorCryptMarketplaceSellerActionType;
  /** Amount for priced actions; null for actions without a price (e.g. `cancelListing`). */
  amount: CollectAmount | null;
  decimalAmount: string | null;
  decimalAmountUsd: string | null;
  paymentAssetChain: ChainKey | null;
  paymentAssetAddress: string | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Status-discriminated view of a seller action, mirroring the
 * `pending | error | success`-style transactional event payloads elsewhere in
 * `EventRecord`. Each branch carries only the fields meaningful in that state:
 * a `txnHash` once the transaction is confirmed and an `errorReason` once the
 * action fails or is parked for review.
 */
export type CollectMarketplaceSellerActionSummary =
  CollectMarketplaceSellerActionSummaryBase &
    (
      | { status: 'created' | 'pending' }
      | { status: 'confirmed'; txnHash: string }
      | { status: 'review'; txnHash: string | null; errorReason: string | null }
      | { status: 'error'; txnHash: string | null; errorReason: string | null }
    );

interface CollectMarketplaceOfferActionSummaryBase {
  id: string;
  /** Client-supplied idempotency key for the offer action. */
  requestId: string;
  nftAddress: string;
  walletAddress: string;
  actionType: CollectorCryptMarketplaceOfferActionType;
  /** Amount for priced actions; null for `cancelOffer`. */
  amount: CollectAmount | null;
  decimalAmount: string | null;
  decimalAmountUsd: string | null;
  paymentAssetChain: ChainKey | null;
  paymentAssetAddress: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export type CollectMarketplaceOfferActionSummary =
  CollectMarketplaceOfferActionSummaryBase &
    (
      | { status: 'created' | 'pending' }
      | { status: 'confirmed'; txnHash: string }
      | { status: 'review'; txnHash: string | null; errorReason: string | null }
      | { status: 'error'; txnHash: string | null; errorReason: string | null }
    );

export interface CollectMarketplaceOfferSummary {
  id: string;
  nftAddress: string;
  walletAddress: string;
  amount: CollectAmount;
  decimalAmount: string;
  decimalAmountUsd: string;
  paymentAssetChain: ChainKey;
  paymentAssetAddress: string;
  status: CollectorCryptMarketplaceOfferStatus;
  openedAt: Date;
  closedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

interface CollectShippingRedemptionSummaryBase {
  id: string;
  /** Client-supplied idempotency key for the redemption. */
  requestId: string;
  walletAddress: string;
  nftAddresses: string[];
  shippingAddressId: string;
  outboundShipmentId: string | null;
  amount: CollectAmount;
  decimalAmount: string;
  decimalAmountUsd: string;
  paymentAssetChain: ChainKey;
  paymentAssetAddress: string;
  burnSignatures: string[] | null;
  cardSnapshots: Collectible[] | null;
  createdAt: Date;
  updatedAt: Date;
}

export type CollectShippingRedemptionSummary =
  CollectShippingRedemptionSummaryBase &
    (
      | { status: 'created' | 'pending' }
      | { status: 'confirmed' }
      | { status: 'review'; errorReason: string | null }
      | { status: 'error'; errorReason: string | null }
    );

export interface CollectMarketplaceOpenOffersAggregate {
  /** Total amount denominated in the payment asset, as a decimal string. */
  decimalAmount: string;
  /** Total USD value at write time, as a decimal string. */
  decimalAmountUsd: string;
  paymentAsset: CollectAsset;
  count: number;
  /** Wallet bucket to attach the open-offers position to. */
  walletAddress: string | null;
}

export interface CollectGachaBuybackSummary {
  id: string;
  /** Client-supplied idempotency key for the buyback. */
  requestId: string;
  walletAddress: string;
  nftAddress: string;
  amount: CollectAmount;
  status: CollectorCryptGachaBuybackStatus;
  source: CollectorCryptGachaBuybackSource;
  buybackTxnHash: string | null;
  errorReason: string | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Per-holding gacha buyback descriptor, enriched onto a held `Collectible` in
 * the collectibles dataset. Only present for cards we sold via gacha that are
 * still actionable:
 * - `eligible`: within the buyback window and Collector Crypt confirmed a quote.
 *   `refundAmount*` is an estimate; the authoritative amount is re-quoted when
 *   the buyback is initiated.
 * - `pending`: a buyback for this NFT is already in flight (or under review).
 *
 * Cards with no actionable buyback carry `null` (the field is omitted from the
 * UI). Bought-back cards leave the wallet, so they simply drop out of holdings.
 */
export type CollectibleGachaBuyback =
  | {
      status: 'eligible';
      amount: CollectAmount;
      /**
       * ms epoch when the buyback window closes, estimated from our open time.
       * `null` for cards we did not sell — CC's `available` flag is the sole
       * authority and there is no app-side window to advertise.
       */
      expiresAt: number | null;
    }
  | {
      status: 'pending';
      buyback: CollectGachaBuybackSummary;
    };
